import { FormattedMessage } from 'react-intl';

import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { LabelM } from '@sorare/core/src/atoms/typography';

export enum TimeFrame {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  THREE_MONTHS = 'THREE_MONTHS',
  SIX_MONTHS = 'SIX_MONTHS',
  YEAR = 'YEAR',
}

const NOW = new Date();

const ONE_DAY_AGO = new Date(NOW.getTime() - 24 * 60 * 60 * 1000);
const ONE_WEEK_AGO = new Date(NOW.getTime() - 7 * 24 * 60 * 60 * 1000);
const ONE_MONTH_AGO = new Date(NOW.getTime() - 30 * 24 * 60 * 60 * 1000);
const THREE_MONTHS_AGO = new Date(NOW.getTime() - 3 * 30 * 24 * 60 * 60 * 1000);
const SIX_MONTHS_AGO = new Date(NOW.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
const ONE_YEAR_AGO = new Date(NOW.getTime() - 365 * 24 * 60 * 60 * 1000);

export const TIME_FRAME_FROM_TO: Record<TimeFrame, { from: Date; to: Date }> = {
  [TimeFrame.DAY]: {
    from: ONE_DAY_AGO,
    to: NOW,
  },
  [TimeFrame.WEEK]: {
    from: ONE_WEEK_AGO,
    to: NOW,
  },
  [TimeFrame.MONTH]: {
    from: ONE_MONTH_AGO,
    to: NOW,
  },
  [TimeFrame.THREE_MONTHS]: {
    from: THREE_MONTHS_AGO,
    to: NOW,
  },
  [TimeFrame.SIX_MONTHS]: {
    from: SIX_MONTHS_AGO,
    to: NOW,
  },
  [TimeFrame.YEAR]: {
    from: ONE_YEAR_AGO,
    to: NOW,
  },
};

const TIMEFRAMES = [
  {
    label: (
      <LabelM bold>
        <FormattedMessage id="time-frame.day" defaultMessage="1 Day" />
      </LabelM>
    ),
    value: TimeFrame.DAY,
  },
  {
    label: (
      <LabelM bold>
        <FormattedMessage id="time-frame.week" defaultMessage="1 Week" />
      </LabelM>
    ),
    value: TimeFrame.WEEK,
  },
  {
    label: (
      <LabelM bold>
        <FormattedMessage id="time-frame.month" defaultMessage="1 Month" />
      </LabelM>
    ),
    value: TimeFrame.MONTH,
  },
  {
    label: (
      <LabelM bold>
        <FormattedMessage
          id="time-frame.three-months"
          defaultMessage="3 Months"
        />
      </LabelM>
    ),
    value: TimeFrame.THREE_MONTHS,
  },
  {
    label: (
      <LabelM bold>
        <FormattedMessage
          id="time-frame.six-months"
          defaultMessage="6 Months"
        />
      </LabelM>
    ),
    value: TimeFrame.SIX_MONTHS,
  },
  {
    label: (
      <LabelM bold>
        <FormattedMessage id="time-frame.year" defaultMessage="1 year" />
      </LabelM>
    ),
    value: TimeFrame.YEAR,
  },
];

type Props = {
  value: TimeFrame;
  onChange: (t: TimeFrame) => void;
};

export const TimeFrameSelector = ({ value, onChange }: Props) => {
  const selectedOption =
    TIMEFRAMES.find(tf => tf.value === value) || TIMEFRAMES[2];
  return (
    <FilterInDropdown buttonLabel={selectedOption.label} buttonSize="compact">
      {({ closeDropdown }) => (
        <RadioGroup
          modalSelect
          options={TIMEFRAMES}
          value={selectedOption.value}
          name="time-frame-selector-chart"
          onChange={tf => {
            onChange(tf);
            closeDropdown();
          }}
        />
      )}
    </FilterInDropdown>
  );
};
